import axiosClient from './axiosClient';

/**
 * Typed axios wrapper for `/api/admin/rbac-v2/tenant-groups` +
 * `/api/admin/rbac-v2/users/:userId/groups` (CPT-1132/1133).
 *
 * Mounted under the super-admin-only `/api/admin` stack; every mutation here
 * is env-local operational RBAC data, so callers use
 * `WriteButton`/`WriteIconButton` with `ignoreEnvReadOnly`.
 */

export type GroupLevel = 'read' | 'write';
export type GroupScope = 'tenant' | 'customer' | 'sponsor';

export interface TenantGroupMember {
  scope_type: GroupScope;
  scope_code: string;
}

export interface TenantGroup {
  id: number;
  name: string;
  level: GroupLevel;
  members: TenantGroupMember[];
  /** Present on list rows so the Groups tab avoids an N+1 count fetch. */
  assigned_user_count?: number;
}

export interface GroupInput {
  name: string;
  level: GroupLevel;
  members: TenantGroupMember[];
}

export interface DeleteGroupResult {
  deleted: true;
  removed_assignments: number;
}

export interface UserGroupAssignment {
  group_id: number;
}

const base = '/api/admin/rbac-v2';

export const tenantGroupsApi = {
  listGroups: async (): Promise<TenantGroup[]> => {
    const { data } = await axiosClient.get<TenantGroup[]>(`${base}/tenant-groups`);
    return data;
  },

  createGroup: async (input: GroupInput): Promise<TenantGroup> => {
    const { data } = await axiosClient.post<TenantGroup>(`${base}/tenant-groups`, input);
    return data;
  },

  updateGroup: async (id: number, input: GroupInput): Promise<TenantGroup> => {
    const { data } = await axiosClient.put<TenantGroup>(`${base}/tenant-groups/${id}`, input);
    return data;
  },

  /** Live re-check ahead of a delete confirm (mirrors the reference's
   * `staleTime: 0` re-fetch) — assignments can change on the Users tab
   * between opens, and a destructive confirm must not flash a stale count. */
  getAssignedUserCount: async (id: number): Promise<number> => {
    const { data } = await axiosClient.get<{ count: number }>(
      `${base}/tenant-groups/${id}/assignment-count`,
    );
    return data.count;
  },

  /** Cascade-unassign (decisions §19) — the server removes every
   * `app_user_tenant_group` row for this group and reports the count. */
  deleteGroup: async (id: number): Promise<DeleteGroupResult> => {
    const { data } = await axiosClient.delete<{ deleted: true; removed_assignments: number }>(
      `${base}/tenant-groups/${id}`,
    );
    return data;
  },

  listUserGroups: async (userId: number): Promise<TenantGroup[]> => {
    const { data } = await axiosClient.get<TenantGroup[]>(`${base}/users/${userId}/groups`);
    return data;
  },

  assignGroup: async (userId: number, groupId: number): Promise<UserGroupAssignment> => {
    const { data } = await axiosClient.post<UserGroupAssignment>(
      `${base}/users/${userId}/groups`,
      { group_id: groupId },
    );
    return data;
  },

  unassignGroup: async (userId: number, groupId: number): Promise<UserGroupAssignment> => {
    const { data } = await axiosClient.delete<UserGroupAssignment>(
      `${base}/users/${userId}/groups/${groupId}`,
    );
    return data;
  },
};
